// AgoniaSquareMarker frontend: passwordless login, click-to-rate stars, and a
// marker search bar. Talks to the AgoniaSquareMarker plugin's HTTP API (behind a
// reverse proxy). All display of descriptions + average stars is baked into the
// marker tooltips server-side; this file adds the interactive bits.
import { S } from "./Squaremap.js";
import L from "leaflet";

let isStaff = false;
let myCanEdit = false; // op / senior+ / owner — may edit marker title+desc on the web
let staffMode = false;
let staffTimer = null;
let staffLayer = null;
let hiddenPlayers = []; // last fetched hidden-player list (staff mode)
const hiddenMarkers = new Map(); // uuid -> L.Marker (for sidebar click -> open popup)
let hiddenFollowing = null; // uuid of a hidden player we are following (staff mode)

// --- white inline icons (currentColor) ----------------------------------
const ICON_PLAYER =
    '<svg viewBox="0 0 24 24" width="15" height="15"><path fill="currentColor" ' +
    'd="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4 0-9 2-9 6v2h18v-2c0-4-5-6-9-6Z"/></svg>';
const ICON_MENU =
    '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" ' +
    'd="M3 6h18a1 1 0 0 0 0-2H3a1 1 0 0 0 0 2Zm18 5H3a1 1 0 0 0 0 2h18a1 1 0 0 0 0-2Zm0 7H3a1 1 0 0 0 0 2h18a1 1 0 0 0 0-2Z"/></svg>';
const ICON_FILTER =
    '<svg viewBox="0 0 24 24" width="16" height="16" style="vertical-align:-3px;margin-right:6px"><path fill="currentColor" ' +
    'd="M22 3H2l8 9.46V19l4 2v-8.54z"/></svg>';
const ICON_EYE =
    '<svg viewBox="0 0 24 24" width="15" height="15" style="vertical-align:-2px;margin-right:6px"><path fill="currentColor" ' +
    'd="M12 5C6.5 5 2.7 8.6 1 12c1.7 3.4 5.5 7 11 7s9.3-3.6 11-7c-1.7-3.4-5.5-7-11-7zm0 11.5A4.5 4.5 0 1 1 12 7.5a4.5 4.5 0 0 1 0 9zm0-7A2.5 2.5 0 1 0 12 14.5 2.5 2.5 0 0 0 12 9.5z"/></svg>';
const ICON_PIN =
    '<svg viewBox="0 0 24 24" width="14" height="14" style="vertical-align:-2px;margin-right:5px"><path fill="currentColor" ' +
    'd="M14 4V2H6v2h2v6l-2 2v2h5v6h2v-6h5v-2l-2-2V4z"/></svg>';

let myPins = new Set(); // marker keys pinned by the logged-in player (personal)

const ICON_COPY =
    '<svg viewBox="0 0 24 24" width="15" height="15"><path fill="currentColor" d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z"/></svg>';
const ICON_TP =
    '<svg viewBox="0 0 24 24" width="15" height="15"><path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"/></svg>';
const ICON_EDIT =
    '<svg viewBox="0 0 24 24" width="13" height="13" style="vertical-align:-2px;margin-right:5px"><path fill="currentColor" d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25zM20.7 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';
let myUuid = null; // undashed uuid of the logged-in player (from /session)
const ICON_COG =
    '<svg viewBox="0 0 24 24" width="19" height="19"><path fill="currentColor" d="M19.14 12.94a7.6 7.6 0 0 0 0-1.88l2-1.58a.5.5 0 0 0 .12-.62l-1.9-3.28a.5.5 0 0 0-.6-.22l-2.36.95a7.2 7.2 0 0 0-1.62-.94l-.36-2.5a.5.5 0 0 0-.5-.42h-3.8a.5.5 0 0 0-.5.42l-.36 2.5c-.58.24-1.12.55-1.62.94l-2.36-.95a.5.5 0 0 0-.6.22L2.74 8.86a.5.5 0 0 0 .12.62l2 1.58a7.6 7.6 0 0 0 0 1.88l-2 1.58a.5.5 0 0 0-.12.62l1.9 3.28a.5.5 0 0 0 .6.22l2.36-.95c.5.39 1.04.7 1.62.94l.36 2.5a.5.5 0 0 0 .5.42h3.8a.5.5 0 0 0 .5-.42l.36-2.5c.58-.24 1.12-.55 1.62-.94l2.36.95a.5.5 0 0 0 .6-.22l1.9-3.28a.5.5 0 0 0-.12-.62zM12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z"/></svg>';

// ---- persistence: a one-year cookie, mirrored into localStorage -----------
// Settings live in a COOKIE so they survive everything that wipes site data but
// keeps cookies (and so they outlive a "clear cached files" sweep). localStorage
// is written too and used as a fallback, which also migrates anyone who already
// had settings from before the cookie existed.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // one year, refreshed on every write
function readCookie(name) {
    const all = String(document.cookie || "").split(";");
    for (const part of all) {
        const eq = part.indexOf("=");
        if (eq > 0 && part.slice(0, eq).trim() === name) {
            try {
                return decodeURIComponent(part.slice(eq + 1).trim());
            } catch {
                return null;
            }
        }
    }
    return null;
}
function writeCookie(name, value) {
    document.cookie =
        name +
        "=" +
        encodeURIComponent(value) +
        ";max-age=" +
        COOKIE_MAX_AGE +
        ";path=/;SameSite=Lax" +
        (location.protocol === "https:" ? ";Secure" : "");
}
/** Read a stored setting: cookie first, then the old localStorage value. */
function readStored(key) {
    const c = readCookie(key);
    if (c !== null) return c;
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}
/** Write to both, so the cookie is the source of truth but nothing is lost. */
function writeStored(key, value) {
    writeCookie(key, value);
    try {
        localStorage.setItem(key, value);
    } catch {
        /* private mode / storage full: the cookie still has it */
    }
}

// ---- per-browser display settings (the cogwheel menu) ---------------------
// Everything here is purely cosmetic, so nothing needs a login or a round-trip
// to the server.
const SET_KEYS = {
    names: "agonia_always_names",
    flashlight: "agonia_flashlight",
    beamAccent: "agonia_beam_accent",
    beamStrength: "agonia_beam_strength",
    accentPlus: "agonia_accent_plus",
};
function readBool(key, def) {
    const v = readStored(key);
    return v === null ? def : v === "1";
}
/** A 0-100 setting. Anything unparseable or out of range falls back to `def`. */
function readPercent(key, def) {
    const v = parseInt(readStored(key), 10);
    return isNaN(v) ? def : Math.min(100, Math.max(0, v));
}
const settings = {
    names: readBool(SET_KEYS.names, true),
    flashlight: readBool(SET_KEYS.flashlight, true),
    beamAccent: readBool(SET_KEYS.beamAccent, false),
    // 0-100: how strong the view cone is. 55 is what it was before the slider.
    beamStrength: readPercent(SET_KEYS.beamStrength, 55),
    accentPlus: readBool(SET_KEYS.accentPlus, false),
};
// "Visa alltid spelarnamn" used to default to off, and every visit re-stamps the
// cookies — so returning visitors carry an explicit "0" that would keep the new
// default from ever reaching them. Nudge them over exactly once.
const NAMES_DEFAULT_FLIP = "agonia_names_default_v2";
if (readStored(NAMES_DEFAULT_FLIP) === null) {
    settings.names = true;
    writeStored(NAMES_DEFAULT_FLIP, "1");
}

// util/Player.js reads this (window global instead of an import, to avoid a cycle)
window.agoniaSettings = settings;

/** Cookie form of a setting. Numbers keep their value; everything else is a flag. */
function storedForm(value) {
    return typeof value === "number" ? String(value) : value ? "1" : "0";
}
function setSetting(key, value) {
    settings[key] = !!value;
    writeStored(SET_KEYS[key], storedForm(settings[key]));
    applySettings();
}
/** Same, for the 0-100 sliders. */
function setPercentSetting(key, value) {
    settings[key] = Math.min(100, Math.max(0, Math.round(value)));
    writeStored(SET_KEYS[key], storedForm(settings[key]));
    applySettings();
}
// Re-stamp every cookie on load so an active visitor's settings never age out.
function refreshSettingCookies() {
    for (const key of Object.keys(SET_KEYS)) {
        writeStored(SET_KEYS[key], storedForm(settings[key]));
    }
    writeStored("agonia_accent", currentAccent());
}
// Settings that are pure CSS become classes (or variables) on <html>; the two
// player-head ones are pushed onto the live markers.
function applySettings() {
    const root = document.documentElement;
    root.classList.toggle("agonia-beam-accent", settings.beamAccent);
    root.classList.toggle("agonia-accent-plus", settings.accentPlus);
    root.style.setProperty("--agonia-beam-op", String(settings.beamStrength / 100));
    if (S && S.playerList && typeof S.playerList.applyDisplaySettings === "function") {
        S.playerList.applyDisplaySettings();
    }
}

// ---- accent colour (round HSV wheel), stored per browser ------------------
function currentAccent() {
    const c = readStored("agonia_accent");
    return /^#[0-9a-fA-F]{6}$/.test(c || "") ? c : "#1f9fd6";
}
function applyAccent(hex) {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) hex = "#1f9fd6";
    document.documentElement.style.setProperty("--sm-accent", hex);
    writeStored("agonia_accent", hex);
    const cur = document.querySelector(".agonia-accent-cur");
    if (cur) cur.style.background = hex;
    const hexEl = document.querySelector(".agonia-hex");
    if (hexEl) hexEl.textContent = hex;
    document.querySelectorAll(".agonia-swatch").forEach((sw) => {
        sw.classList.toggle("on", (sw.dataset.hex || "").toLowerCase() === hex.toLowerCase());
    });
}

// Two-tone logo: recolour the coloured ("red") part of images/logo-color.png to a
// FIXED gray (the same #232a36 the chrome buttons use) while keeping white parts
// white. Deliberately NOT tied to the accent, so changing the palette leaves the
// logo untouched. Falls back to the plain masked logo.png from CSS if the image
// isn't present.
const LOGO_GRAY = [0x23, 0x2a, 0x36]; // matches --sm-bg-2 (the button colour)
let _logoImg = null;
let _logoBad = false;
function recolorLogo() {
    if (_logoBad) return;
    const el = document.getElementById("smplus-logo");
    if (!el) return;
    const paint = () => {
        try {
            const w = _logoImg.naturalWidth,
                h = _logoImg.naturalHeight;
            const c = document.createElement("canvas");
            c.width = w;
            c.height = h;
            const ctx = c.getContext("2d");
            ctx.drawImage(_logoImg, 0, 0);
            const id = ctx.getImageData(0, 0, w, h);
            const d = id.data;
            const [ar, ag, ab] = LOGO_GRAY;
            for (let i = 0; i < d.length; i += 4) {
                if (d[i + 3] === 0) continue;
                const r = d[i],
                    g = d[i + 1],
                    b = d[i + 2];
                // white/near-white stays white; everything coloured becomes the gray
                if (!(r > 175 && g > 175 && b > 175)) {
                    d[i] = ar;
                    d[i + 1] = ag;
                    d[i + 2] = ab;
                }
            }
            ctx.putImageData(id, 0, 0);
            el.style.webkitMask = "none";
            el.style.mask = "none";
            el.style.backgroundColor = "transparent";
            el.style.backgroundImage = "url(" + c.toDataURL("image/png") + ")";
            el.style.backgroundSize = "contain";
            el.style.backgroundRepeat = "no-repeat";
            el.style.backgroundPosition = "center";
            el.style.filter = "drop-shadow(0 2px 6px rgba(0,0,0,0.6))";
        } catch {
            /* keep the CSS mask fallback */
        }
    };
    if (_logoImg && _logoImg.complete && _logoImg.naturalWidth) {
        paint();
        return;
    }
    _logoImg = new Image();
    _logoImg.onload = paint;
    _logoImg.onerror = () => {
        _logoBad = true; // no colour image -> keep the accent-masked logo.png
    };
    _logoImg.src = "images/logo-color.png";
}
// apply immediately (before render) so there's no flash of the default colour
applyAccent(currentAccent());
document.documentElement.classList.toggle("agonia-beam-accent", settings.beamAccent);
document.documentElement.classList.toggle("agonia-accent-plus", settings.accentPlus);

// --- colour maths ---
function hsvToRgb(h, s, v) {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    // no initialisers: the branch chain below is exhaustive (it ends in `else`)
    let r, g, b;
    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];
    return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}
function rgbToHsv(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
        min = Math.min(r, g, b),
        d = max - min;
    let h = 0;
    if (d) {
        if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h *= 60;
        if (h < 0) h += 360;
    }
    return [h, max ? d / max : 0, max];
}
function hexToRgb(hex) {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}
function rgbToHex(r, g, b) {
    return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

// A few good-looking starting points, so nobody has to hunt on the wheel.
const ACCENT_PRESETS = ["#1f9fd6", "#3b82f6", "#8b5cf6", "#ec4899", "#ef4444", "#f59e0b", "#22c55e", "#14b8a6"];

/** One "label + switch" row for the settings menu. */
function settingRow(label, hint, checked, onChange) {
    const row = document.createElement("label");
    row.className = "agonia-set-row";
    const text = document.createElement("span");
    text.className = "agonia-set-text";
    text.innerHTML =
        `<span class="agonia-set-label">${escapeHtml(label)}</span>` +
        (hint ? `<span class="agonia-set-hint">${escapeHtml(hint)}</span>` : "");
    const sw = document.createElement("span");
    sw.className = "agonia-switch";
    const box = document.createElement("input");
    box.type = "checkbox";
    box.checked = !!checked;
    box.addEventListener("change", () => onChange(box.checked));
    sw.appendChild(box);
    sw.appendChild(document.createElement("i"));
    row.appendChild(text);
    row.appendChild(sw);
    return row;
}

/** One "label + slider" row for the settings menu (0-100). Fires while dragging,
 *  so the effect it drives previews live on the map behind the menu. */
function sliderRow(label, hint, value, onInput) {
    const row = document.createElement("div");
    row.className = "agonia-set-row agonia-set-slider";
    const text = document.createElement("span");
    text.className = "agonia-set-text";
    text.innerHTML =
        `<span class="agonia-set-label">${escapeHtml(label)}</span>` +
        (hint ? `<span class="agonia-set-hint">${escapeHtml(hint)}</span>` : "");
    const slider = document.createElement("input");
    slider.type = "range";
    slider.className = "agonia-val-slider";
    slider.min = "0";
    slider.max = "100";
    slider.value = String(value);
    slider.addEventListener("input", (e) => {
        e.stopPropagation();
        onInput(Number(slider.value));
    });
    row.appendChild(text);
    row.appendChild(slider);
    return row;
}

function setSection(title) {
    const el = document.createElement("div");
    el.className = "agonia-set-sec";
    el.textContent = title;
    return el;
}

// Cogwheel button (bottom-left) opening the per-player settings menu: accent
// colour wheel + map display toggles. Everything is stored in this browser only.
function buildSettingsButton() {
    const corner = document.querySelector(".leaflet-bottom.leaflet-left");
    if (!corner) {
        setTimeout(buildSettingsButton, 300);
        return;
    }
    if (document.getElementById("agonia-settings-wrap")) return;

    const ctrl = document.createElement("div");
    ctrl.className = "leaflet-control agonia-settings-control";
    // Interacting with this control (buttons, wheel, slider) must never pan/zoom the map.
    L.DomEvent.disableClickPropagation(ctrl);
    L.DomEvent.disableScrollPropagation(ctrl);
    const wrap = document.createElement("div");
    wrap.id = "agonia-settings-wrap";
    const btn = document.createElement("button");
    btn.id = "agonia-settings-btn";
    btn.title = "Inställningar – sparas bara hos dig";
    btn.innerHTML = ICON_COG + '<span class="agonia-accent-cur"></span>';

    const pop = document.createElement("div");
    pop.className = "agonia-settings-pop";
    const head = document.createElement("div");
    head.className = "agonia-set-head";
    head.textContent = "Inställningar";
    pop.appendChild(head);
    pop.appendChild(setSection("Välj färg"));
    const size = 168;
    const wheel = document.createElement("canvas");
    wheel.className = "agonia-wheel";
    wheel.width = size;
    wheel.height = size;
    const dot = document.createElement("div");
    dot.className = "agonia-wheel-dot";
    const wheelWrap = document.createElement("div");
    wheelWrap.className = "agonia-wheel-wrap";
    wheelWrap.appendChild(wheel);
    wheelWrap.appendChild(dot);
    const slider = document.createElement("input");
    slider.type = "range";
    slider.className = "agonia-val-slider";
    slider.min = "0";
    slider.max = "100";
    const hexEl = document.createElement("div");
    hexEl.className = "agonia-hex";
    const swatches = document.createElement("div");
    swatches.className = "agonia-swatches";
    pop.appendChild(wheelWrap);
    pop.appendChild(slider);
    pop.appendChild(hexEl);
    pop.appendChild(swatches);

    const ctx = wheel.getContext("2d");
    const R = size / 2;
    let cur = rgbToHsv(...hexToRgb(currentAccent())); // [h, s, v]

    const drawWheel = (v) => {
        const img = ctx.createImageData(size, size);
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                const dx = x - R,
                    dy = y - R,
                    r = Math.sqrt(dx * dx + dy * dy);
                const i = (y * size + x) * 4;
                if (r > R) {
                    img.data[i + 3] = 0;
                    continue;
                }
                let h = (Math.atan2(dy, dx) * 180) / Math.PI;
                if (h < 0) h += 360;
                const [rr, gg, bb] = hsvToRgb(h, Math.min(r / R, 1), v);
                img.data[i] = rr;
                img.data[i + 1] = gg;
                img.data[i + 2] = bb;
                img.data[i + 3] = 255;
            }
        }
        ctx.putImageData(img, 0, 0);
    };
    const placeDot = () => {
        const [h, s] = cur;
        const rad = (h * Math.PI) / 180;
        const r = s * R;
        dot.style.left = R + Math.cos(rad) * r + "px";
        dot.style.top = R + Math.sin(rad) * r + "px";
    };
    const commit = () => {
        const [h, s, v] = cur;
        const hex = rgbToHex(...hsvToRgb(h, s, v));
        applyAccent(hex);
        placeDot();
    };
    const pickFrom = (e) => {
        const rect = wheel.getBoundingClientRect();
        const dx = e.clientX - rect.left - R;
        const dy = e.clientY - rect.top - R;
        let h = (Math.atan2(dy, dx) * 180) / Math.PI;
        if (h < 0) h += 360;
        const s = Math.min(Math.sqrt(dx * dx + dy * dy) / R, 1);
        cur = [h, s, cur[2]];
        commit();
    };

    let dragging = false;
    wheel.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        dragging = true;
        wheel.setPointerCapture(e.pointerId);
        pickFrom(e);
    });
    wheel.addEventListener("pointermove", (e) => {
        if (dragging) pickFrom(e);
    });
    wheel.addEventListener("pointerup", () => {
        dragging = false;
    });
    slider.addEventListener("input", (e) => {
        e.stopPropagation();
        cur = [cur[0], cur[1], slider.value / 100];
        drawWheel(cur[2]);
        commit();
    });

    // quick-pick swatches (they also re-sync the wheel/slider to the chosen colour)
    for (const hex of ACCENT_PRESETS) {
        const sw = document.createElement("button");
        sw.className = "agonia-swatch";
        sw.dataset.hex = hex;
        sw.style.background = hex;
        sw.title = hex;
        sw.addEventListener("click", (e) => {
            e.stopPropagation();
            cur = rgbToHsv(...hexToRgb(hex));
            slider.value = String(Math.round(cur[2] * 100));
            drawWheel(cur[2]);
            commit();
        });
        swatches.appendChild(sw);
    }

    // --- map display toggles ---
    pop.appendChild(setSection("Karta"));
    pop.appendChild(
        settingRow("Visa alltid spelarnamn", "Namnen syns hela tiden", settings.names, (on) => setSetting("names", on)),
    );
    pop.appendChild(
        settingRow("Visa spelares riktning", "Ett ljus visar vart de tittar", settings.flashlight, (on) => {
            setSetting("flashlight", on);
            beamRow.classList.toggle("agonia-set-off", !on);
            strengthRow.classList.toggle("agonia-set-off", !on);
        }),
    );
    const beamRow = settingRow("Ljuset i din färg", "Annars vitt ljus", settings.beamAccent, (on) =>
        setSetting("beamAccent", on),
    );
    beamRow.classList.add("agonia-set-sub");
    beamRow.classList.toggle("agonia-set-off", !settings.flashlight);
    pop.appendChild(beamRow);

    const strengthRow = sliderRow("Ljusstyrka", "Hur starkt ljuset lyser", settings.beamStrength, (v) =>
        setPercentSetting("beamStrength", v),
    );
    strengthRow.classList.add("agonia-set-sub");
    strengthRow.classList.toggle("agonia-set-off", !settings.flashlight);
    pop.appendChild(strengthRow);

    // --- how far the accent colour reaches ---
    pop.appendChild(setSection("Extra"));
    pop.appendChild(
        settingRow("Mer färg", "Färgen syns på fler ställen", settings.accentPlus, (on) =>
            setSetting("accentPlus", on),
        ),
    );

    btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const opening = !wrap.classList.contains("open");
        wrap.classList.toggle("open", opening);
        if (opening) {
            cur = rgbToHsv(...hexToRgb(currentAccent()));
            slider.value = String(Math.round(cur[2] * 100));
            drawWheel(cur[2]);
            placeDot();
        }
    });
    pop.addEventListener("click", (e) => e.stopPropagation());
    document.addEventListener("click", (e) => {
        if (!wrap.contains(e.target)) wrap.classList.remove("open");
    });
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") wrap.classList.remove("open");
    });

    wrap.appendChild(btn);
    wrap.appendChild(pop);
    ctrl.appendChild(wrap);
    corner.insertBefore(ctrl, corner.firstChild);
    slider.value = String(Math.round(cur[2] * 100));
    drawWheel(cur[2]);
    placeDot();
    applyAccent(currentAccent());
}

const LS_TOKEN = "agonia_token";
const LS_NAME = "agonia_name";

let apiBase = "/agonia/";
let apiPort = null;
let markers = [];
let pollTimer = null;

const state = {
    get token() {
        return localStorage.getItem(LS_TOKEN);
    },
    get name() {
        return localStorage.getItem(LS_NAME);
    },
    set(token, name) {
        localStorage.setItem(LS_TOKEN, token);
        localStorage.setItem(LS_NAME, name);
    },
    clear() {
        localStorage.removeItem(LS_TOKEN);
        localStorage.removeItem(LS_NAME);
    },
};

// "relative" | "port" once we know which one the server actually answers on, so we
// stop re-probing (and 404-logging) the other on every single call.
let apiResolved = null;
function apiUrls(path) {
    const base = apiBase.replace(/\/$/, "");
    const rel = { kind: "relative", url: base + "/" + path };
    const port = apiPort
        ? { kind: "port", url: `${location.protocol}//${location.hostname}:${apiPort}${base}/${path}` }
        : null;
    if (apiResolved === "relative") return [rel];
    if (apiResolved === "port" && port) return [port];
    // unknown yet: relative (reverse proxy) first, then the direct port fallback
    return port ? [rel, port] : [rel];
}
// Returns a reachable Response (any HTTP status except 404, which means "wrong
// base – try the fallback"). Throws only if nothing is reachable. Once a base
// answers, it's remembered so later calls don't keep hitting the 404 path.
async function api(path, opts) {
    let lastErr;
    for (const u of apiUrls(path)) {
        try {
            const r = await fetch(u.url, opts);
            if (r.status === 404) {
                lastErr = new Error("404");
                continue;
            }
            apiResolved = u.kind; // this base works — use only it from now on
            return r;
        } catch (e) {
            lastErr = e;
        }
    }
    throw lastErr || new Error("unreachable");
}
function form(obj) {
    return {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: Object.entries(obj)
            .map(([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(v))
            .join("&"),
    };
}

async function loadConfig() {
    try {
        const r = await fetch("agonia-web.json", { cache: "no-store" });
        if (r.ok) {
            const j = await r.json();
            if (j.apiBase) apiBase = j.apiBase;
            if (j.port) apiPort = j.port;
        }
    } catch {
        /* defaults */
    }
}
async function loadMarkers() {
    try {
        const r = await fetch("agonia-markers.json", { cache: "no-store" });
        if (r.ok) markers = await r.json();
    } catch {
        /* none */
    }
}
// Map of squaremap layer-id -> min zoom (category "distance"). Written by the
// add-on so the per-category zoom feature works on STOCK squaremap (no fork).
async function loadCategoryZoom() {
    try {
        const r = await fetch("agonia-categories.json", { cache: "no-store" });
        if (r.ok) {
            window.agoniaCategoryZoom = await r.json();
            // Apply immediately (the map may have built its layers before this loaded).
            const w = S.worldList && S.worldList.curWorld;
            if (w && typeof w.updateZoomVisibility === "function") w.updateZoomVisibility();
        }
    } catch {
        /* none */
    }
}
// What each online player is doing (elytra / minecart / boat / riding), written
// by the addon once a second. Drives the little badge on each player head.
async function loadPlayerActivity() {
    try {
        const r = await fetch("agonia-activity.json", { cache: "no-store" });
        if (r.ok) {
            // dashed uuids on disk, undashed everywhere in squaremap's frontend
            const raw = await r.json();
            const norm = {};
            for (const k in raw) norm[String(k).replace(/-/g, "")] = raw[k];
            window.agoniaActivity = norm;
        }
    } catch {
        /* none */
    }
}
async function loadPlayerStatus() {
    try {
        const r = await fetch("agonia-players.json", { cache: "no-store" });
        if (r.ok) {
            // The addon keys this map by dashed UUIDs, but squaremap's player
            // markers use UNDASHED UUIDs. Normalise to undashed so both normal
            // and hidden players resolve their badge status.
            const raw = await r.json();
            const norm = {};
            for (const k in raw) norm[String(k).replace(/-/g, "")] = raw[k];
            window.agoniaPlayerStatus = norm;
        }
    } catch {
        /* none */
    }
}

// ---- UI -------------------------------------------------------------------

let els = {};

function buildUI() {
    const panel = document.createElement("div");
    panel.id = "agonia-panel";

    // search
    const search = document.createElement("div");
    search.id = "agonia-search";
    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "Sök";
    input.autocomplete = "off";
    const results = document.createElement("div");
    results.id = "agonia-results";
    search.appendChild(input);
    search.appendChild(results);

    // account
    const account = document.createElement("div");
    account.id = "agonia-account";

    panel.appendChild(search);
    panel.appendChild(account);
    document.body.appendChild(panel);

    els = { panel, input, results, account };

    input.addEventListener("input", renderResults);
    input.addEventListener("focus", renderResults);
    input.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;
        const co = parseCoords(els.input.value);
        if (co) {
            // coordinates: fly to them in the CURRENT world, no dropdown
            S.map.panTo(S.toLatLng(co.x, co.z));
            results.classList.remove("open");
            els.input.blur();
        }
    });
    document.addEventListener("click", (e) => {
        if (!search.contains(e.target)) results.classList.remove("open");
    });

    renderAccount();
}

// Parse a coordinate search: "x z" or "x y z" (y ignored) -> {x, z}, else null.
function parseCoords(raw) {
    const m = String(raw)
        .trim()
        .match(/^(-?\d+(?:\.\d+)?)[ ,]+(-?\d+(?:\.\d+)?)(?:[ ,]+(-?\d+(?:\.\d+)?))?$/);
    if (!m) return null;
    const a = parseFloat(m[1]);
    const b = parseFloat(m[2]);
    const c = m[3] !== undefined ? parseFloat(m[3]) : null;
    // 2 numbers => (x, z); 3 numbers => (x, y, z) so use the 1st and 3rd.
    return c === null ? { x: a, z: b } : { x: a, z: c };
}

function renderResults() {
    const q = els.input.value.trim().toLowerCase();
    els.results.innerHTML = "";
    // Coordinates get no dropdown (press Enter to fly there).
    if (!q || parseCoords(els.input.value)) {
        els.results.classList.remove("open");
        return;
    }

    const rows = [];
    const seen = new Set();

    // online players (from squaremap's live player list)
    if (S.playerList && S.playerList.players) {
        for (const pl of S.playerList.players.values()) {
            const dn = (pl.displayName || "").replace(/<[^>]*>/g, "");
            if ((pl.name || "").toLowerCase().includes(q) || dn.toLowerCase().includes(q)) {
                seen.add(pl.uuid);
                rows.push({ kind: "player", uuid: pl.uuid, name: pl.name, world: pl.world, x: pl.x, z: pl.z });
            }
        }
    }
    // hidden players (staff only)
    if (isStaff) {
        for (const hp of hiddenPlayers) {
            if (seen.has(hp.uuid) || !(hp.name || "").toLowerCase().includes(q)) continue;
            rows.push({
                kind: "player",
                uuid: hp.uuid,
                name: hp.name,
                world: hp.world,
                x: hp.x,
                z: hp.z,
                hidden: true,
            });
        }
    }
    // markers
    for (const m of markers) {
        if (m.name.toLowerCase().includes(q)) rows.push({ kind: "marker", marker: m });
    }

    const matches = rows.slice(0, 8);
    if (matches.length === 0) {
        els.results.classList.remove("open");
        return;
    }
    for (const r of matches) {
        const row = document.createElement("div");
        row.className = "agonia-result";
        if (r.kind === "player") {
            row.innerHTML =
                `<img class="r-icon r-head${r.hidden ? " r-hidden" : ""}" src="${headUrl(r.uuid, r.name)}" alt="">` +
                `<span class="r-text">` +
                `<span class="r-name">${escapeHtml(r.name)}</span>` +
                `<span class="r-meta">${r.hidden ? "Gömd spelare" : "Spelare"} · ${Math.round(r.x)}, ${Math.round(r.z)}</span>` +
                `</span>`;
            row.addEventListener("click", () => {
                if (r.hidden) {
                    // follow the hidden player (staff), like a normal player follow
                    if (S.playerList) S.playerList.followPlayerMarker(null);
                    hiddenFollowing = r.uuid;
                    refreshHiddenSidebar();
                    if (hiddenMarkers.has(r.uuid)) {
                        setTimeout(() => hiddenMarkers.get(r.uuid) && hiddenMarkers.get(r.uuid).openPopup(), 250);
                    }
                }
                flyTo({ x: r.x, z: r.z, world: r.world });
                els.results.classList.remove("open");
                els.input.blur();
            });
        } else {
            const m = r.marker;
            const icon = m.icon || "default";
            row.innerHTML =
                `<img class="r-icon" src="images/icon/registered/${encodeURIComponent(icon)}.png" alt="">` +
                `<span class="r-text">` +
                `<span class="r-name">${escapeHtml(m.name)}</span>` +
                `<span class="r-meta">${Math.round(m.x)}, ${Math.round(m.z)}</span>` +
                `</span>`;
            row.addEventListener("click", () => {
                flyTo(m);
                els.results.classList.remove("open");
                els.input.blur();
            });
        }
        els.results.appendChild(row);
    }
    els.results.classList.add("open");
}

function flyTo(m) {
    const ll = S.toLatLng(m.x, m.z);
    const cur = S.worldList && S.worldList.curWorld;
    if (cur && cur.name === m.world) {
        // same world: just pan (keep zoom; never reload layers or hide distance markers)
        S.map.panTo(ll);
    } else if (S.worldList && S.worldList.worlds && S.worldList.worlds.has(m.world)) {
        S.worldList.showWorld(m.world, () => S.map.panTo(ll));
    } else {
        S.map.panTo(ll);
    }
}

function renderAccount() {
    els.account.innerHTML = "";
    // Tell the player list who "I" am (for top-of-list + highlight).
    const prevMe = window.agoniaMe || null;
    window.agoniaMe = state.token && state.name ? state.name : null;
    if (prevMe !== window.agoniaMe && S.playerList && typeof S.playerList.reorderMe === "function") {
        S.playerList.reorderMe();
    }
    if (state.token && state.name) {
        // clickable "who" chip: shows YOUR profile card below (no map jump)
        const who = document.createElement("button");
        who.type = "button";
        who.className = "agonia-who";
        who.title = "Visa din profil";
        who.innerHTML =
            '<span class="agonia-who-icon">' +
            ICON_PLAYER +
            '</span><span class="agonia-me-name">' +
            escapeHtml(state.name) +
            '</span><span class="agonia-me-dot" title="Online på servern"></span>';
        who.addEventListener("click", (e) => {
            e.stopPropagation();
            toggleMyProfile(who);
        });
        const out = document.createElement("button");
        out.className = "agonia-btn";
        out.textContent = "Logga ut";
        out.addEventListener("click", logout);
        els.account.appendChild(who);
        els.account.appendChild(out);
        updateMeDot();
    } else {
        const btn = document.createElement("button");
        btn.className = "agonia-btn";
        btn.textContent = "Logga in";
        btn.addEventListener("click", openLogin);
        els.account.appendChild(btn);
    }
    renderStaffButton();
}

// Match the head-glide duration (CSS --agonia-move-dur) to the server's player
// update interval, so heads travel continuously toward their next position.
function syncMoveDuration() {
    try {
        const pt = S.worldList && S.worldList.curWorld && S.worldList.curWorld.player_tracker;
        let secs = pt && pt.update_interval ? Number(pt.update_interval) : 1;
        if (!isFinite(secs) || secs <= 0) secs = 1;
        document.documentElement.style.setProperty("--agonia-move-dur", secs + "s");
    } catch {
        /* keep the 1s default */
    }
}

// Is the logged-in player currently on the server? (shown normally OR hidden.)
function isMeOnline() {
    const nm = (state.name || "").toLowerCase();
    if (!nm) return false;
    if (S.playerList && S.playerList.players) {
        for (const pl of S.playerList.players.values()) {
            if ((pl.name || "").toLowerCase() === nm) return true;
        }
    }
    for (const p of hiddenPlayers) {
        if ((p.name || "").toLowerCase() === nm) return true;
    }
    return false;
}

// Show the little "online" dot beside your name only while you're on the server.
function updateMeDot() {
    const dot = els.account && els.account.querySelector(".agonia-me-dot");
    if (dot) dot.classList.toggle("online", isMeOnline());
}

// Toggle your own profile card (head + crown + badges) right below the name chip.
function toggleMyProfile(anchorBtn) {
    const existing = document.getElementById("agonia-me-pop");
    if (existing) {
        existing.remove();
        return;
    }
    const pop = document.createElement("div");
    pop.id = "agonia-me-pop";
    pop.className = "agonia-me-pop";
    pop.innerHTML = buildPlayerPopupHtml(myUuid || state.name, state.name, false);
    pop.addEventListener("click", (e) => e.stopPropagation());
    document.body.appendChild(pop);
    const r = anchorBtn.getBoundingClientRect();
    const pr = pop.getBoundingClientRect();
    pop.style.top = r.bottom + 8 + "px";
    pop.style.left = Math.max(8, Math.min(r.left, window.innerWidth - pr.width - 8)) + "px";
    const close = (e) => {
        if (!pop.contains(e.target) && e.target !== anchorBtn) {
            pop.remove();
            document.removeEventListener("click", close);
        }
    };
    setTimeout(() => document.addEventListener("click", close), 0);
}

function openLogin() {
    els.account.innerHTML = "";
    const box = document.createElement("div");
    box.className = "agonia-login";

    const close = document.createElement("button");
    close.className = "agonia-close";
    close.textContent = "✕";
    close.title = "Stäng";
    close.addEventListener("click", () => {
        if (pollTimer) clearInterval(pollTimer);
        renderAccount();
    });
    box.appendChild(close);

    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "Minecraft-namn";
    input.autocomplete = "off";

    const hint = document.createElement("div");
    hint.className = "agonia-hint";
    hint.textContent = "OBS! För att logga in måste du vara whitelistad och inne på servern";

    const go = document.createElement("button");
    go.className = "agonia-btn";
    go.textContent = "Begär";
    const status = document.createElement("div");
    status.className = "agonia-status";

    const submit = () => {
        const name = input.value.trim();
        if (name) requestLogin(name, status);
    };
    go.addEventListener("click", submit);
    input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") submit();
    });

    box.appendChild(input);
    box.appendChild(hint);
    box.appendChild(go);
    box.appendChild(status);
    els.account.appendChild(box);
    input.focus();
}

async function requestLogin(name, status) {
    if (pollTimer) clearInterval(pollTimer);
    status.textContent = "Begär…";
    try {
        const r = await api("login", form({ username: name }));
        const j = await r.json();
        if (!j.id) {
            status.textContent = j.error || "Misslyckades.";
            return;
        }
        const id = j.id;
        const cmd = j.command || "/kartalogin " + id;
        // Show the command to copy + run in-game. The code only works for the exact
        // username entered, so nobody gets spammed and nobody can log in as someone else.
        status.innerHTML = "";
        const instr = document.createElement("div");
        instr.className = "agonia-login-instr";
        instr.textContent = "Kopiera kommandot och kör det i spelet för att logga in:";
        const copyBtn = document.createElement("button");
        copyBtn.className = "agonia-btn";
        copyBtn.textContent = "Kopiera Kommando";
        copyBtn.addEventListener("click", () => {
            copyText(cmd);
            copyBtn.textContent = "Kopierat!";
            setTimeout(() => (copyBtn.textContent = "Kopiera Kommando"), 1500);
        });
        status.appendChild(instr);
        status.appendChild(copyBtn);
        pollTimer = setInterval(async () => {
            try {
                const pr = await api("poll?id=" + encodeURIComponent(id));
                const pj = await pr.json();
                if (pj.status === "approved") {
                    clearInterval(pollTimer);
                    state.set(pj.token, pj.username);
                    validateSession(); // fetch staff flag + re-render
                } else if (pj.status === "offline") {
                    clearInterval(pollTimer);
                    status.textContent = "Du är inte inloggad på servern.";
                } else if (pj.status === "denied") {
                    clearInterval(pollTimer);
                    status.textContent = "Inloggning nekad.";
                } else if (pj.status === "expired" || pj.status === "unknown") {
                    clearInterval(pollTimer);
                    status.textContent = "Begäran utgick, försök igen.";
                }
            } catch {
                /* keep polling */
            }
        }, 1500);
    } catch {
        status.textContent = "Kunde inte nå servern.";
    }
}

async function logout() {
    const t = state.token;
    state.clear();
    isStaff = false;
    myCanEdit = false;
    myUuid = null;
    myPins = new Set();
    refreshPinned();
    if (staffMode) {
        toggleStaffMode(); // turn off + clear hidden markers
    }
    renderAccount();
    if (t) {
        try {
            await api("logout", form({ token: t }));
        } catch {
            /* ignore */
        }
    }
}

async function validateSession() {
    if (!state.token) {
        isStaff = false;
        myCanEdit = false;
        return;
    }
    try {
        const r = await api("session?token=" + encodeURIComponent(state.token));
        const j = await r.json();
        if (!j.valid) {
            state.clear();
            isStaff = false;
            myCanEdit = false;
            myUuid = null;
            myPins = new Set();
            refreshPinned();
        } else {
            if (j.username) localStorage.setItem(LS_NAME, j.username);
            isStaff = !!j.staff;
            myCanEdit = !!j.canedit;
            myUuid = j.uuid || null;
            loadMyPins();
        }
    } catch {
        /* keep token, server may be down */
    }
    renderAccount();
}

function headUrl(uuid, name) {
    const np =
        S.worldList &&
        S.worldList.curWorld &&
        S.worldList.curWorld.player_tracker &&
        S.worldList.curWorld.player_tracker.nameplates;
    const tpl = (np && np.heads_url) || "https://mc-heads.net/avatar/{uuid}/32";
    return tpl.replace(/{uuid}/g, uuid).replace(/{name}/g, name || "");
}

const CROWN_SVG =
    '<svg viewBox="0 0 24 24" width="14" height="14"><path fill="currentColor" ' +
    'd="M2 7l5 4 5-7 5 7 5-4-2.2 12H4.2L2 7z"/></svg>';
const SHIELD_SVG =
    '<svg viewBox="0 0 24 24" width="13" height="13"><path fill="currentColor" stroke="rgba(0,0,0,.55)" ' +
    'stroke-width="1" d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5z"/></svg>';
// staff role badges (highest one shown). ägare = a second (bottom) crown instead of a shield.
const ROLE_META = {
    junior: { label: "Junior Hjälpare", color: "#86efac" },
    hjalpare: { label: "Hjälpare", color: "#fde047" },
    senior: { label: "Senior Hjälpare", color: "#fca5a5" },
    ledare: { label: "Ledare", color: "#ef4444" },
};
// staff role shield (bottom-left of head). ägare is NOT here (it's a top crown).
function roleBadgeHtml(role) {
    const rm = ROLE_META[role];
    if (!rm) return "";
    return `<span class="pinfo-shield" style="color:${rm.color}" title="${rm.label}">${SHIELD_SVG}</span>`;
}
window.agoniaRoleBadge = roleBadgeHtml; // used by util/Player.js too

// Top crown: ägare (red) takes priority over prime; else diamond/gold prime.
function crownHtml(st) {
    if (st.agare) return `<span class="pinfo-crown crown-agare" title="Server Ägare">${CROWN_SVG}</span>`;
    if (st.prime === "diamond") return `<span class="pinfo-crown crown-aqua">${CROWN_SVG}</span>`;
    if (st.prime === "gold") return `<span class="pinfo-crown crown-gold">${CROWN_SVG}</span>`;
    return "";
}
// Membership text badge(s): ägare -> "SERVER ÄGARE" (suppresses prime); else prime; + veteran.
function membershipBadges(st) {
    let b = "";
    if (st.agare) b += `<div class="pinfo-badge badge-agare">SERVER ÄGARE</div>`;
    else if (st.prime === "diamond") b += `<div class="pinfo-badge badge-diamond">DIAMOND PRIME</div>`;
    else if (st.prime === "gold") b += `<div class="pinfo-badge badge-prime">PRIME</div>`;
    if (st.veteran) b += `<div class="pinfo-badge badge-veteran">VETERAN</div>`;
    return b;
}
window.agoniaCrown = crownHtml;
window.agoniaMembership = membershipBadges;

// Bedrock players (name starts with "-") have no NameMC profile -> no button.
function nameMcHtml(uuid, name) {
    if (typeof name === "string" && name.startsWith("-")) return "";
    return (
        `<a class="pinfo-namemc" href="https://namemc.com/profile/${encodeURIComponent(uuid)}" ` +
        `target="_blank" rel="noopener">NameMC</a>`
    );
}
window.agoniaNameMc = nameMcHtml;

function playerStatusOf(uuid) {
    const m = window.agoniaPlayerStatus;
    if (!m) return {};
    return m[uuid] || m[String(uuid).replace(/-/g, "")] || {};
}

// True if this uuid is already shown in squaremap's normal (public) player list.
// Used to avoid drawing a duplicate "hidden" entry/marker for the same player.
function isShownNormally(uuid) {
    if (!S.playerList || !S.playerList.players) return false;
    const n = String(uuid || "")
        .replace(/-/g, "")
        .toLowerCase();
    for (const pl of S.playerList.players.values()) {
        if (
            String(pl.uuid || "")
                .replace(/-/g, "")
                .toLowerCase() === n
        )
            return true;
    }
    return false;
}

/** Build a player click-popup (head + crown + name + NameMC + badges [+ hidden note]). */
function buildPlayerPopupHtml(uuid, name, hidden) {
    const st = playerStatusOf(uuid);
    const head = headUrl(uuid, name);
    const note = hidden ? `<div class="pinfo-hidden">Denna spelare har gömt sig från andra spelare!</div>` : "";
    return (
        `<div class="agonia-pinfo">` +
        `<div class="pinfo-head-wrap"><img class="pinfo-head" src="${head}">${crownHtml(st)}${st.agare ? "" : roleBadgeHtml(st.role)}</div>` +
        `<div class="pinfo-col">` +
        `<div class="pinfo-name">${escapeHtml(name)}</div>` +
        nameMcHtml(uuid, name) +
        membershipBadges(st) +
        note +
        `</div></div>`
    );
}

function renderStaffButton() {
    if (!isStaff || !els.account) {
        return;
    }
    const btn = document.createElement("button");
    btn.className = "agonia-btn agonia-staff-btn" + (staffMode ? " agonia-staff-on" : "");
    btn.innerHTML = ICON_EYE + "Visa gömda";
    btn.addEventListener("click", toggleStaffMode);
    els.account.appendChild(btn);
}

function toggleStaffMode() {
    staffMode = !staffMode;
    renderAccount();
    if (staffMode) {
        if (!staffLayer) {
            staffLayer = L.layerGroup();
        }
        staffLayer.addTo(S.map);
        refreshHidden();
        staffTimer = setInterval(refreshHidden, 2000);
    } else {
        if (staffTimer) {
            clearInterval(staffTimer);
            staffTimer = null;
        }
        if (staffLayer) {
            staffLayer.clearLayers();
        }
        hiddenPlayers = [];
        hiddenMarkers.clear();
        hiddenFollowing = null;
        refreshHiddenSidebar();
    }
}

async function refreshHidden() {
    if (!staffMode || !state.token || !staffLayer) {
        return;
    }
    let list;
    try {
        const r = await api("hidden?token=" + encodeURIComponent(state.token));
        list = await r.json();
    } catch {
        return;
    }
    if (!Array.isArray(list)) {
        return;
    }
    // Never list a player twice: if someone is already shown in squaremap's normal
    // player list (e.g. yourself, pinned + highlighted at the top), drop their hidden
    // duplicate so each player appears exactly once — not as both shown and hidden.
    list = list.filter((p) => !isShownNormally(p.uuid));
    hiddenPlayers = list;

    // Update markers IN PLACE (don't clear+recreate) so an open popup stays open
    // and doesn't fade away every refresh — exactly like a normal player marker.
    const seen = new Set();
    for (const p of list) {
        // map head only for the current world; sidebar lists every hidden player
        if (S.worldList.curWorld && p.world !== S.worldList.curWorld.name) {
            continue;
        }
        seen.add(p.uuid);
        const ll = S.toLatLng(p.x, p.z);
        let m = hiddenMarkers.get(p.uuid);
        if (m) {
            m._agp = p; // keep latest data for the context menu
            m.setLatLng(ll);
            // refresh popup content only while it's closed (avoid clobbering an open one)
            if (!m.isPopupOpen()) {
                m.setPopupContent(buildPlayerPopupHtml(p.uuid, p.name, true));
            }
        } else {
            m = L.marker(ll, {
                icon: L.icon({
                    iconUrl: headUrl(p.uuid, p.name),
                    iconSize: [20, 20],
                    iconAnchor: [10, 10],
                    className: "agonia-playerhead agonia-hidden-head",
                }),
            });
            m._agp = p;
            m.bindTooltip(p.name, { direction: "top" });
            m.bindPopup(buildPlayerPopupHtml(p.uuid, p.name, true));
            m.on("contextmenu", (e) => {
                if (e.originalEvent) e.originalEvent.preventDefault();
                L.DomEvent.stopPropagation(e);
                const d = m._agp || p;
                if (window.agoniaOnPlayerContext) {
                    window.agoniaOnPlayerContext({ name: d.name, x: d.x, z: d.z, world: d.world }, e.originalEvent);
                }
            });
            m.addTo(staffLayer);
            hiddenMarkers.set(p.uuid, m);
        }
    }
    // drop markers for players no longer hidden / not in this world
    for (const [uuid, m] of [...hiddenMarkers]) {
        if (!seen.has(uuid)) {
            staffLayer.removeLayer(m);
            hiddenMarkers.delete(uuid);
        }
    }

    // follow a hidden player (like normal follow): pan to them each refresh
    if (hiddenFollowing) {
        if (S.playerList && S.playerList.following) {
            hiddenFollowing = null; // a normal-player follow takes precedence
        } else {
            const hp = list.find((p) => p.uuid === hiddenFollowing);
            if (hp) {
                flyTo({ x: hp.x, z: hp.z, world: hp.world });
            } else {
                hiddenFollowing = null; // no longer hidden/online
            }
        }
    }
    refreshHiddenSidebar();
}

// Show hidden players in the sidebar player list (staff only), with a red glow.
// Clicking flies to them and opens their info popup.
function refreshHiddenSidebar() {
    const fieldset = S.sidebar && S.sidebar.players && S.sidebar.players.element;
    if (!fieldset) {
        return;
    }
    fieldset.querySelectorAll("a.agonia-hidden-entry").forEach((el) => el.remove());
    if (!staffMode) {
        return;
    }
    for (const p of hiddenPlayers) {
        const a = document.createElement("a");
        a.className = "agonia-hidden-entry" + (hiddenFollowing === p.uuid ? " following" : "");
        a.innerHTML =
            `<img class="head" src="${headUrl(p.uuid, p.name)}" alt="">` + `<span>${escapeHtml(p.name)}</span>`;
        a.addEventListener("click", (e) => {
            e.stopPropagation();
            if (hiddenFollowing === p.uuid) {
                hiddenFollowing = null; // click again to stop following
                a.classList.remove("following");
                return;
            }
            // follow this hidden player (clear any normal-player follow first)
            if (S.playerList) S.playerList.followPlayerMarker(null);
            hiddenFollowing = p.uuid;
            refreshHiddenSidebar();
            flyTo({ x: p.x, z: p.z, world: p.world });
            const mk = hiddenMarkers.get(p.uuid);
            if (mk) {
                setTimeout(() => mk.openPopup(), 250);
            }
        });
        fieldset.appendChild(a);
    }
}

// ---- ratings inside marker popups -----------------------------------------

let current = 0; // last-known own rating for the open popup

function enhanceRating(popupEl) {
    const span = popupEl.querySelector(".sm-rating[data-marker]");
    if (!span || span.dataset.enhanced) return;
    span.dataset.enhanced = "1";
    const markerKey = span.dataset.marker;

    const widget = document.createElement("div");
    widget.className = "agonia-rate";
    const row = document.createElement("div");
    row.className = "agonia-rate-stars";
    const label = document.createElement("div");
    label.className = "agonia-rate-label";

    current = 0;
    const stars = [];
    const paint = (n) => stars.forEach((el, idx) => el.classList.toggle("on", idx < n));
    for (let i = 1; i <= 5; i++) {
        const s = document.createElement("span");
        s.className = "agonia-star";
        s.textContent = "★";
        s.addEventListener("mouseenter", () => paint(i));
        s.addEventListener("mouseleave", () => paint(current));
        s.addEventListener("click", () => submitRating(markerKey, i, label, stars));
        row.appendChild(s);
        stars.push(s);
    }
    label.textContent = state.token ? "Betygsätt" : "Logga in för att betygsätta";
    paint(0);
    widget.appendChild(row);
    widget.appendChild(label);
    span.appendChild(widget);

    // staff: show who rated
    if (isStaff) {
        const raters = document.createElement("div");
        raters.className = "agonia-raters";
        raters.textContent = "…";
        span.appendChild(raters);
        api("raters?token=" + encodeURIComponent(state.token) + "&marker=" + encodeURIComponent(markerKey))
            .then((r) => r.json())
            .then((list) => {
                if (!Array.isArray(list) || list.length === 0) {
                    raters.textContent = "Inga betyg än.";
                    return;
                }
                raters.innerHTML =
                    "<b>Betyg:</b> " + list.map((x) => escapeHtml(x.name) + " (" + x.stars + "★)").join(", ");
            })
            .catch(() => {
                raters.textContent = "";
            });
    }
}

// Personal pin button inside a marker's click popup (logged-in players only).
function enhancePin(popupEl) {
    const slot = popupEl.querySelector(".sm-pinslot[data-marker]");
    if (!slot || slot.dataset.enhanced) return;
    slot.dataset.enhanced = "1";
    if (!state.token) return; // only logged-in players can pin

    const key = slot.dataset.marker;
    const btn = document.createElement("button");
    btn.className = "agonia-pin-btn";
    const paint = () => {
        const on = myPins.has(key);
        btn.classList.toggle("on", on);
        btn.innerHTML = ICON_PIN; // icon only, no text
        btn.title = on ? "Ta bort din fästa markör" : "Fäst denna markör (bara du ser den)";
    };
    paint();
    btn.addEventListener("click", async () => {
        const on = !myPins.has(key);
        if (on) myPins.add(key);
        else myPins.delete(key);
        paint();
        refreshPinned();
        try {
            const r = await api("pin", form({ token: state.token, marker: key, pinned: on }));
            if (r.status === 401) {
                state.clear();
                myPins.delete(key);
                paint();
                refreshPinned();
                renderAccount();
            }
        } catch {
            /* keep optimistic state */
        }
    });
    slot.appendChild(btn);
}

function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, "&quot;");
}

// Owner/staff "edit title + description" affordance in the marker popup.
function enhanceOwnerEdit(popupEl) {
    const slot = popupEl.querySelector(".sm-pinslot[data-marker]");
    if (!slot || slot.dataset.ownerEnhanced) return;
    const key = slot.dataset.marker;
    const m = markers.find((x) => x.key === key);
    if (!m) return;
    const mine = myUuid && Array.isArray(m.owners) && m.owners.includes(myUuid);
    if (!myCanEdit && !mine) return; // only op/senior+ or an owner may edit on the web
    slot.dataset.ownerEnhanced = "1";
    const editBtn = document.createElement("button");
    editBtn.className = "agonia-edit-btn";
    editBtn.innerHTML = ICON_EDIT + "Redigera";
    editBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        openOwnerEditor(popupEl, key, m, editBtn);
    });
    slot.appendChild(editBtn);
}

// Count words in any language (Swedish å/ä/ö and English both count): any run of
// non-whitespace characters is one word.
const MAX_DESC_WORDS = 150;
function wordCount(s) {
    const t = String(s == null ? "" : s).trim();
    return t ? t.split(/\s+/).length : 0;
}

function openOwnerEditor(popupEl, key, m, editBtn) {
    if (popupEl.querySelector(".agonia-edit-box")) return;
    const descEl = popupEl.querySelector(".sm-desc");
    const curTitle = m.name || "";
    const curDesc = descEl ? descEl.textContent : "";
    const box = document.createElement("div");
    box.className = "agonia-edit-box";
    box.innerHTML =
        `<input class="agonia-edit-title" value="${escapeAttr(curTitle)}" placeholder="Titel">` +
        `<textarea class="agonia-edit-desc" placeholder="Beskrivning" rows="3">${escapeHtml(curDesc)}</textarea>` +
        `<div class="agonia-edit-count"><span class="agonia-wc">0</span> / ${MAX_DESC_WORDS} ord</div>` +
        `<div class="agonia-edit-actions">` +
        `<button class="agonia-btn agonia-edit-save">Spara</button>` +
        `<button class="agonia-edit-cancel">Avbryt</button></div>`;
    box.addEventListener("click", (e) => e.stopPropagation());
    if (editBtn) editBtn.style.display = "none";
    (popupEl.querySelector(".leaflet-popup-content") || popupEl).appendChild(box);

    // Live word counter (max 150), works for Swedish and English text.
    const descField = box.querySelector(".agonia-edit-desc");
    const countEl = box.querySelector(".agonia-edit-count");
    const wcEl = box.querySelector(".agonia-wc");
    const saveBtn = box.querySelector(".agonia-edit-save");
    const updateCount = () => {
        const n = wordCount(descField.value);
        wcEl.textContent = n;
        const over = n > MAX_DESC_WORDS;
        countEl.classList.toggle("over", over);
        saveBtn.disabled = over;
    };
    descField.addEventListener("input", updateCount);
    updateCount();

    box.querySelector(".agonia-edit-cancel").addEventListener("click", () => {
        box.remove();
        if (editBtn) editBtn.style.display = "";
    });
    box.querySelector(".agonia-edit-save").addEventListener("click", async () => {
        const title = box.querySelector(".agonia-edit-title").value.trim();
        const desc = box.querySelector(".agonia-edit-desc").value;
        if (wordCount(desc) > MAX_DESC_WORDS) {
            showToast(`Beskrivningen får vara max ${MAX_DESC_WORDS} ord.`);
            return;
        }
        try {
            const r = await api("markeredit", form({ token: state.token, marker: key, title, desc }));
            if (r.status === 403) {
                showToast("Du får inte redigera den.");
                return;
            }
            showToast("Sparat!");
            S.map.closePopup();
            loadMarkers().then(refreshPinned);
        } catch {
            showToast("Kunde inte spara.");
        }
    });
}

async function submitRating(markerKey, stars, labelEl, starEls) {
    if (!state.token) {
        labelEl.textContent = "Logga in först";
        openLogin();
        return;
    }
    try {
        const r = await api("rate", form({ token: state.token, marker: markerKey, stars }));
        if (r.status === 401) {
            state.clear();
            renderAccount();
            labelEl.textContent = "Sessionen gick ut, logga in";
            return;
        }
        const j = await r.json();
        if (j.ok) {
            current = j.mine;
            starEls.forEach((el, idx) => el.classList.toggle("on", idx < j.mine));
            labelEl.textContent = `Du: ${j.mine}★ · snitt ${Number(j.avg).toFixed(1)} (${j.count})`;
        } else {
            labelEl.textContent = j.error || "Misslyckades";
        }
    } catch {
        labelEl.textContent = "Fel";
    }
}

function escapeHtml(s) {
    return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// ---- init -----------------------------------------------------------------

// Turn squaremap's hover-expanding layer control into a proper click-to-open
// "Filter" button (with a close X) that lives in the top panel beside search.
function relocateFilters() {
    const ctrl = document.querySelector(".leaflet-control-layers:not(.link):not(.coordinates)");
    if (!ctrl) {
        setTimeout(relocateFilters, 300);
        return;
    }
    const list = ctrl.querySelector(".leaflet-control-layers-list");
    if (!list) {
        setTimeout(relocateFilters, 300);
        return;
    }

    // our own wrapper: [Filter button] + [dropdown panel with X]
    const wrap = document.createElement("div");
    wrap.id = "agonia-filter";

    const btn = document.createElement("button");
    btn.id = "agonia-filter-btn";
    btn.className = "agonia-btn";
    btn.innerHTML = ICON_FILTER + "Filter";

    const panel = document.createElement("div");
    panel.id = "agonia-filter-panel";

    const head = document.createElement("div");
    head.className = "agonia-filter-head";
    head.innerHTML = "<span>Filter</span>";
    const x = document.createElement("button");
    x.className = "agonia-filter-close";
    x.innerHTML = "✕";
    x.title = "Stäng";
    head.appendChild(x);
    panel.appendChild(head);

    // move Leaflet's live list into our panel (Leaflet keeps updating it in place)
    panel.appendChild(list);

    wrap.appendChild(btn);
    wrap.appendChild(panel);
    els.panel.insertBefore(wrap, els.account); // search | filter | account

    // hide the now-empty original Leaflet control (and stop its hover behaviour)
    ctrl.style.display = "none";

    const open = (v) => {
        panel.classList.toggle("open", v);
        btn.classList.toggle("agonia-open", v); // accent-tinted while the panel is up
    };
    btn.addEventListener("click", (e) => {
        e.stopPropagation();
        open(!panel.classList.contains("open"));
    });
    x.addEventListener("click", (e) => {
        e.stopPropagation();
        open(false);
    });
    panel.addEventListener("click", (e) => e.stopPropagation());
    document.addEventListener("click", (e) => {
        if (!wrap.contains(e.target)) open(false);
    });
}

// A small top-right button to show/hide the sidebar. The icon adapts to state:
// a "menu" (3 lines) when closed, a "close" (X) when open.
const ICON_CLOSE =
    '<svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M18.3 5.7a1 1 0 0 0-1.4 0L12 10.6 7.1 5.7A1 1 0 0 0 5.7 7.1L10.6 12l-4.9 4.9a1 1 0 1 0 1.4 1.4L12 13.4l4.9 4.9a1 1 0 0 0 1.4-1.4L13.4 12l4.9-4.9a1 1 0 0 0 0-1.4z"/></svg>';
function buildSidebarToggle() {
    const btn = document.createElement("button");
    btn.id = "agonia-sidebar-toggle";
    const paint = (open) => {
        btn.innerHTML = open ? ICON_CLOSE : ICON_MENU;
        btn.title = open ? "Dölj sidofältet" : "Visa sidofältet";
        btn.classList.toggle("active", open);
    };
    // Match the button's initial icon to the sidebar's actual visibility.
    paint(!!(S.sidebar && S.sidebar.showSidebar));
    btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!S.sidebar) {
            return;
        }
        const open = !S.sidebar.showSidebar;
        S.sidebar.show(open);
        paint(open);
    });
    document.body.appendChild(btn);
}

// Load THIS player's personal pins (marker keys) from the server.
async function loadMyPins() {
    if (!state.token) {
        myPins = new Set();
        refreshPinned();
        return;
    }
    try {
        const r = await api("pins?token=" + encodeURIComponent(state.token));
        const list = await r.json();
        myPins = new Set(Array.isArray(list) ? list : []);
    } catch {
        /* keep what we have */
    }
    refreshPinned();
}

// A "Fästa" (pinned) section in the sidebar, between Worlds and Players, listing
// the markers THIS logged-in player has pinned. Click to fly there.
let pinnedEl = null;
function refreshPinned() {
    if (!S.sidebar || !S.sidebar.sidebar || !S.sidebar.players) {
        return;
    }
    const pins = markers.filter((m) => myPins.has(m.key));
    if (!pinnedEl) {
        pinnedEl = document.createElement("fieldset");
        pinnedEl.id = "agonia-pinned";
        S.sidebar.sidebar.insertBefore(pinnedEl, S.sidebar.players.element);
    }
    if (pins.length === 0) {
        pinnedEl.style.display = "none";
        return;
    }
    pinnedEl.style.display = "";
    pinnedEl.innerHTML = "<legend>Fästa</legend>";
    for (const m of pins) {
        const a = document.createElement("a");
        a.innerHTML =
            `<img src="images/icon/registered/${encodeURIComponent(m.icon || "default")}.png" alt="">` +
            escapeHtml(m.name);
        a.addEventListener("click", (e) => {
            e.stopPropagation();
            flyTo(m);
        });
        pinnedEl.appendChild(a);
    }
}
function initPinned() {
    if (!S.sidebar || !S.sidebar.players) {
        setTimeout(initPinned, 400);
        return;
    }
    refreshPinned();
}

// ---- right-click context menu (map + players) -----------------------------
let ctxEl = null;
let ctxMarker = null;

function closeContextMenu() {
    if (ctxEl) {
        ctxEl.remove();
        ctxEl = null;
    }
    if (ctxMarker) {
        S.map.removeLayer(ctxMarker);
        ctxMarker = null;
    }
}

function copyText(text) {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
            return;
        }
    } catch {
        /* fall through */
    }
    fallbackCopy(text);
}
function fallbackCopy(text) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
        document.execCommand("copy");
    } catch {
        /* ignore */
    }
    ta.remove();
}

function showToast(msg) {
    let t = document.getElementById("agonia-toast");
    if (!t) {
        t = document.createElement("div");
        t.id = "agonia-toast";
        document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add("show");
    clearTimeout(t._hide);
    t._hide = setTimeout(() => t.classList.remove("show"), 1700);
}

function openContextMenu(mouseEv, header, items, latlng) {
    closeContextMenu();
    // Pin a crosshair at the clicked spot; it stays until you click elsewhere.
    if (latlng) {
        ctxMarker = L.marker(latlng, {
            icon: L.divIcon({ className: "agonia-ctx-cross", html: "", iconSize: [22, 22] }),
            interactive: false,
            pane: "nameplate",
        }).addTo(S.map);
    }
    const menu = document.createElement("div");
    menu.className = "agonia-ctx";
    if (header) {
        const h = document.createElement("div");
        h.className = "agonia-ctx-head";
        h.textContent = header;
        menu.appendChild(h);
    }
    for (const it of items) {
        if (!it) continue;
        if (it.sep) {
            const s = document.createElement("div");
            s.className = "agonia-ctx-sep";
            menu.appendChild(s);
            continue;
        }
        const b = document.createElement("button");
        b.className = "agonia-ctx-item";
        b.innerHTML = (it.icon || "") + "<span>" + it.label + "</span>";
        b.addEventListener("click", (e) => {
            e.stopPropagation();
            it.action();
            closeContextMenu();
        });
        menu.appendChild(b);
    }
    document.body.appendChild(menu);
    const r = menu.getBoundingClientRect();
    const mx = mouseEv ? mouseEv.clientX : window.innerWidth / 2;
    const my = mouseEv ? mouseEv.clientY : window.innerHeight / 2;
    menu.style.left = Math.max(6, Math.min(mx, window.innerWidth - r.width - 8)) + "px";
    menu.style.top = Math.max(6, Math.min(my, window.innerHeight - r.height - 8)) + "px";
    ctxEl = menu;
}

function staffAction(type, params) {
    if (!state.token) return;
    api("action", form(Object.assign({ token: state.token, type }, params))).catch(() => {});
}

function openMapMenu(latlng, mouseEv, showCross) {
    const p = S.toPoint(latlng);
    // Use floor to exactly match the block coordinate shown in the readout.
    const x = Math.floor(p.x);
    const z = Math.floor(p.y);
    const world = S.worldList && S.worldList.curWorld ? S.worldList.curWorld.name : "";
    const items = [
        {
            icon: ICON_COPY,
            label: "Kopiera koordinater",
            action: () => {
                copyText(x + ", " + z);
                showToast("Kopierad: " + x + ", " + z);
            },
        },
    ];
    if (isStaff) {
        items.push({ sep: true });
        items.push({
            icon: ICON_TP,
            label: "Teleportera hit",
            action: () => {
                staffAction("tpcoords", { world, x, z });
                showToast("Teleporterar…");
            },
        });
    }
    // Only mark the spot with the crosshair for empty-ground clicks; right-clicking
    // a marker/player shows just the menu (no circle).
    openContextMenu(mouseEv, x + ", " + z, items, showCross ? latlng : null);
}

function openPlayerMenu(pl, mouseEv) {
    const x = Math.floor(pl.x);
    const z = Math.floor(pl.z);
    const items = [
        {
            icon: ICON_COPY,
            label: "Kopiera koordinater",
            action: () => {
                copyText(x + ", " + z);
                showToast("Kopierad: " + x + ", " + z);
            },
        },
    ];
    if (isStaff) {
        items.push({ sep: true });
        items.push({
            icon: ICON_TP,
            label: "Teleportera till spelaren",
            action: () => {
                staffAction("tpplayer", { target: pl.name });
                showToast("Teleporterar till " + pl.name + "…");
            },
        });
    }
    openContextMenu(mouseEv, pl.name + " · " + x + ", " + z, items, null);
}
// let Player.js (and hidden markers) open the player menu on right-click
window.agoniaOnPlayerContext = openPlayerMenu;

function initContextMenu() {
    S.map.on("contextmenu", (e) => {
        if (e.originalEvent) e.originalEvent.preventDefault();
        // no crosshair circle when the right-click landed on a marker/player icon
        const t = e.originalEvent && e.originalEvent.target;
        const onMarker = !!(t && t.closest && t.closest(".leaflet-marker-icon"));
        openMapMenu(e.latlng, e.originalEvent, !onMarker);
    });
    S.map.on("movestart", closeContextMenu);
    document.addEventListener("click", closeContextMenu);
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeContextMenu();
    });
}

async function init() {
    if (!S || !S.map) {
        setTimeout(init, 200);
        return;
    }
    await loadConfig();
    await loadMarkers();
    await loadCategoryZoom();
    loadPlayerStatus();
    loadPlayerActivity();
    buildUI();
    recolorLogo(); // two-tone logo (fixed gray + white) once the DOM logo element exists
    buildSidebarToggle();
    buildSettingsButton();
    applySettings();
    refreshSettingCookies(); // push the one-year expiry out on every visit
    relocateFilters();
    initPinned();
    initContextMenu();
    validateSession();
    loadMyPins();
    S.map.on("popupopen", (e) => {
        const el = e.popup.getElement();
        if (el) {
            enhanceRating(el);
            enhancePin(el);
            enhanceOwnerEdit(el);
            // Let the popup appear instantly, THEN enable the "glide with the head"
            // transition (two frames later) so it never slides in on open.
            el.classList.remove("agonia-follow");
            requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add("agonia-follow")));
        }
    });
    S.map.on("popupclose", (e) => {
        const el = e.popup.getElement();
        if (el) el.classList.remove("agonia-follow");
    });
    // (Always-on nameplates are their own markers now — see util/Player.js — so
    // they glide with the head on the plain marker rules, no follow-class needed.)
    // While zooming, heads/popups use the map's zoom curve (see .agonia-zooming CSS)
    // instead of the movement glide, so they scale/settle with the map. Keep the flag
    // until just after the 0.25s zoom settle, and survive rapid successive zooms.
    let zoomTimer = null;
    S.map.on("zoomstart", () => {
        if (zoomTimer) {
            clearTimeout(zoomTimer);
            zoomTimer = null;
        }
        document.body.classList.add("agonia-zooming");
    });
    S.map.on("zoomend", () => {
        if (zoomTimer) clearTimeout(zoomTimer);
        zoomTimer = setTimeout(() => {
            document.body.classList.remove("agonia-zooming");
            zoomTimer = null;
        }, 300);
        // Re-run the per-category "distance" gating for the zoom we just landed on.
        // Nothing else does: World.updateZoomVisibility() is otherwise only reached
        // from the 5-second markers.json refresh, so a category would keep the
        // visibility of whatever zoom happened to be current at the last refresh and
        // then flip seconds later, seemingly at random.
        const world = S.worldList && S.worldList.curWorld;
        if (world && typeof world.updateZoomVisibility === "function") {
            world.updateZoomVisibility();
        }
    });
    setInterval(() => loadMarkers().then(refreshPinned), 30000);
    setInterval(loadCategoryZoom, 30000);
    setInterval(loadPlayerStatus, 5000);
    setInterval(loadPlayerActivity, 1000); // must keep up with the heads themselves
    setInterval(updateMeDot, 4000); // keep the "online" dot in sync with server state
    syncMoveDuration();
    setInterval(syncMoveDuration, 10000); // adapt if the world / update interval changes
    // re-check staff status periodically (so the button appears even if it was
    // detected after page load), but don't disturb an open login menu
    setInterval(() => {
        if (state.token) validateSession();
    }, 20000);
}

if (document.readyState === "complete" || document.readyState === "interactive") {
    setTimeout(init, 0);
} else {
    window.addEventListener("load", init);
}
